/**
 * PortfoliYo Lab — the authentication API client (PB-4).
 *
 * Separate from `lib/api.ts` on purpose, and it is the one place a second copy of anything is
 * justified in this frontend. That module is Trade's client: its `getMe` returns Trade's
 * `AuthStatusResponse` (with `trading_mode` and `live_confirmed`, which a research session does not
 * have) and its `logout` posts to a route that deletes a row from Trade's `sessions`. Lab's four
 * auth routes are Lab's own, with their own shapes, served by the Lab process — importing Trade's
 * client for them would make the types lie about what comes back.
 *
 * Everything else the Lab pages call still comes from `lib/api.ts`. The shared source tree is
 * deliberate (IL-D11); this is the exception, not a precedent.
 *
 * Same-origin `/api`, always. The Lab frontend is served by the Lab process, which serves these
 * routes — that is what "single-origin" means here, and it is why there is no base-URL setting to
 * point this somewhere else.
 */

const API_BASE = '/api';

export interface LabUser {
  id: number;
  email: string;
  role: string;
}

export interface LabSessionStatus {
  authenticated: boolean;
  user: LabUser | null;
}

/** Raised for a non-2xx response, carrying the server's `detail` where there is one. */
export class LabAuthError extends Error {
  readonly status: number;
  /** Seconds to wait, from `Retry-After` — set only for a 429 from the resend throttle. */
  readonly retryAfterSeconds?: number;

  // Fields assigned in the body rather than declared as constructor parameters: this project
  // builds with `erasableSyntaxOnly`, which rejects parameter properties because they are the one
  // piece of TypeScript that emits runtime code rather than being erased.
  constructor(message: string, status: number, retryAfterSeconds?: number) {
    super(message);
    this.name = 'LabAuthError';
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // The session cookie is HttpOnly, so the browser attaches it and JavaScript never sees it.
    // `same-origin` is the default for fetch, stated explicitly because the whole design depends
    // on it.
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    const retryAfter = response.headers.get('retry-after');
    let detail = `Request failed (${response.status})`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === 'string') detail = payload.detail;
    } catch {
      // A non-JSON error body (a proxy's 502 page, say) is not worth failing differently over.
    }
    throw new LabAuthError(detail, response.status, retryAfter ? Number(retryAfter) : undefined);
  }

  return response.json() as Promise<T>;
}

export const labAuthApi = {
  /**
   * Ask for a login code.
   *
   * Resolves identically whether or not the address has an account — the server answers the same
   * either way, deliberately, so the login form cannot be used to discover who has one.
   */
  requestCode: (email: string) =>
    post<{ message: string }>('/auth/request-code', { email }),

  /** Exchange a code for a session. The `lab_session` cookie arrives on this response. */
  verifyCode: (email: string, code: string) =>
    post<LabUser>('/auth/verify-code', { email, code }),

  /** End the session. Served by Lab since PB-4 — `lab_sessions` is Lab's to write. */
  logout: () => post<{ message: string }>('/auth/logout'),

  /**
   * Who is signed in.
   *
   * 200 with `authenticated: false` when nobody is, never a 401 — the shell has to tell "signed
   * out" from "this endpoint is broken", and a 401 is indistinguishable between the two.
   */
  getSession: async (): Promise<LabSessionStatus> => {
    const response = await fetch(`${API_BASE}/auth/me`, { credentials: 'same-origin' });
    if (!response.ok) throw new LabAuthError('Session check failed', response.status);
    return response.json() as Promise<LabSessionStatus>;
  },
};
